/**
 * Order Activation — the wire shape, the filter, and the export format.
 *
 * ⚠ HARD RULE 9, and this file is where it is kept: **one predicate, one place.** The
 * table, the KPI cards, the three charts and the Excel export must all reduce the same
 * row set through the same two steps — `visibleRows` (who may see it) then
 * `applyFilters` (what they asked for). The mockups already do this deliberately:
 * `CS.exportRows` applies the rule the table renders under. If the export ever grows
 * its own filter, a contractor eventually downloads a competitor's work.
 *
 * Everything here is a pure function over plain rows, so all of it is tested without a
 * database — which is the point, because the numbers it produces are the ones a
 * contractor's management will be held to.
 */
import { matchesScope } from "./auth.js";
import { ageBucketOf, ageInDays, isoDateUtc, isoWeek, isoWeekKey } from "./time.js";
/** `Chaussée de Bruxelles 144 bus 0017`, as `CS.address` builds it. */
export function formatAddress(row) {
    let out = row.streetName;
    if (row.houseNumberExtension)
        out += ` ${row.houseNumberExtension}`;
    if (row.busNumber)
        out += ` bus ${row.busNumber}`;
    return out;
}
// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary — measured against production 16 Sep 2026
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The order types in the backlog, with the label and pill tone from the mockups.
 *
 * Ubi: *"my priority is the installations. The cancellations, the patches and stuff are
 * less important."* That distinction is `order_type`, which is why it was added to the
 * query. An unknown code renders as itself rather than being dropped — DMP may add one.
 */
export const ORDER_TYPES = [
    { code: "INSTALLATION", label: "Installation", tone: "blue" },
    { code: "CEASE", label: "Cease", tone: "slate" },
    { code: "PATCH", label: "Patch", tone: "violet" },
    { code: "SWAP_SPLITTER", label: "Swap Splitter", tone: "purple" },
];
export function orderTypeLabel(code) {
    return ORDER_TYPES.find((t) => t.code === code)?.label ?? code;
}
export function orderTypeTone(code) {
    return ORDER_TYPES.find((t) => t.code === code)?.tone ?? "slate";
}
/** COCON connection statuses in scope. MTK means activated and is filtered out by THE query. */
export const CONNECTION_STATUSES = [
    { code: "IDP", tone: "blue" },
    { code: "PASS", tone: "amber" },
    { code: "IHB", tone: "violet" },
];
export function connectionStatusTone(code) {
    return CONNECTION_STATUSES.find((s) => s.code === code)?.tone ?? "slate";
}
/**
 * Derive the scope from an authenticated user. The ONE place this is decided.
 *
 * ⚠ `contractorCodes` being an empty array means "mapped to no DMP code" — never "no
 * filter". That distinction is the difference between a misconfigured company seeing
 * nothing and a misconfigured company seeing its competitors, which is the worst
 * failure this application could have.
 */
export function viewerScope(user) {
    const isInternalUser = user.role === "SYSTEM_ADMIN" || user.role === "UNIFIBER_INTERNAL";
    return {
        companyId: isInternalUser ? null : user.companyId,
        contractorCodes: isInternalUser ? null : user.dmpContractorCodes,
    };
}
/**
 * The rows a viewer may see at all, before any filter they chose.
 *
 * ⚠ A CONTRACTOR SEES THE ORDERS DMP ASSIGNS TO THEM, AND NOTHING ELSE — whatever a
 * drawing's audience says. Hugues, 26 Oct 2026, from Constructel's seat: *"even if a POP
 * is shared between multiple subcos, a subco should still only see his orders and not
 * those of the others"*.
 *
 * It was not always so, and the history is why this comment is long. From 17 Sep to
 * 26 Oct 2026 a drawing opened to BOTH contractors pooled its backlog: each saw every row
 * whose contractor was anywhere in the audience. Ubi had asked for it — *"both subcos at
 * the same time please, because some areas were re-assigned between the two, so there is
 * still a backlog of overlapping orders"* — because DMP's `contractor` column was stale for
 * those areas. In practice it put Circet's orders in front of Constructel wherever a
 * drawing was open to both: 127 of them in JEMSAM-1A-B001 alone, 226 in all on 26 Oct, and
 * into Constructel's Excel exports. Do not bring the pool back to fix a stale assignment;
 * the assignment is fixed in DMP.
 *
 * So the audience decides ONE thing now, per viewer: whether their own orders in that
 * drawing are OPEN to them or flagged Hidden (`drawingOpen`, since 13 Oct 2026 — Hugues:
 * *"we should show, even the hidden ones (but with a filter and a flag)"*). Opening a
 * drawing to Constructel as well changes nothing for Circet, and hiding it from
 * Constructel takes nothing from Circet.
 *
 * ⚠ Rows with NO contractor (20 in production) reach internal users ONLY. They belong to
 * nobody, so nobody can claim them — still an open question for Ubi.
 */
export function visibleRows(rows, scope) {
    // Internal roles are not scoped: every row, open or hidden.
    if (scope.contractorCodes === null)
        return [...rows];
    // A contractor with no company cannot be told whether a drawing is open to them. Belt
    // and braces — the column is NOT NULL — but the failure mode of getting this wrong is a leak.
    if (!scope.companyId)
        return [];
    // ⚠ A company holding NO DMP contractor code sees nothing — never everything. Such
    // companies exist on purpose: homes-passed partners (Walfit, S30, Jacops…) are scoped on
    // their Monday labels only, because DMP never writes their name on an order (hard rule
    // 32). `matchesScope` would refuse every row anyway; this says so where it is decided.
    if (scope.contractorCodes.length === 0)
        return [];
    const companyId = scope.companyId;
    const out = [];
    for (const row of rows) {
        // Their own, by DMP's assignment. `matchesScope` refuses a null contractor, which is
        // what keeps unassigned rows internal-only.
        if (!matchesScope(row.contractor, scope.contractorCodes))
            continue;
        out.push(asSeenBy(row, companyId));
    }
    return out;
}
/**
 * The row as one contractor may see it — always a COPY, because the snapshot rows are
 * cached and shared with Unifiber's view, and changing one in place would change it for
 * everybody until the cache expired.
 *
 * ⚠ The audience shrinks to the viewer alone. The full audience would tell Constructel
 * which drawings Ubi has opened to Circet and not to them — a decision about somebody
 * else. `drawingOpen` is recomputed for the viewer for the same reason: a drawing open to
 * Circet alone is Hidden to Constructel, whatever `audience.length > 0` says.
 *
 * The technician is left as it is: every row reaching here is the viewer's own, so hard
 * rule 36 (names on their own rows only) holds by construction.
 */
function asSeenBy(row, companyId) {
    const openToViewer = row.drawingAudience.includes(companyId);
    return {
        ...row,
        drawingAudience: openToViewer ? [companyId] : [],
        drawingOpen: openToViewer,
    };
}
/**
 * Apply the viewer's chosen filters.
 *
 * ⚠ Runs AFTER `visibleRows`, never instead of it. A filter is a convenience the viewer
 * controls; visibility is not. Nothing here can widen the set it is given — in
 * particular `contractors` can only narrow, so a contractor passing
 * `?contractors=circet` on someone else's report still gets their own rows and nothing
 * more. There is a test for exactly that.
 */
export function applyFilters(rows, filters) {
    const search = filters.search?.trim().toLowerCase();
    const contractors = filters.contractors?.length ? new Set(filters.contractors) : null;
    const orderTypes = filters.orderTypes?.length ? new Set(filters.orderTypes) : null;
    const pops = filters.pops?.length ? new Set(filters.pops) : null;
    const statuses = filters.connectionStatuses?.length ? new Set(filters.connectionStatuses) : null;
    const buckets = filters.ageBuckets?.length ? new Set(filters.ageBuckets) : null;
    const cities = filters.cities?.length ? new Set(filters.cities) : null;
    const projectnames = filters.projectnames?.length ? new Set(filters.projectnames) : null;
    const jvid = needle(filters.jvid);
    const technician = needle(filters.technician);
    const orderId = needle(filters.orderId);
    const utac = needle(filters.utac);
    const address = needle(filters.address);
    return rows.filter((row) => {
        if (contractors && !contractors.has(row.contractor ?? "unassigned"))
            return false;
        if (orderTypes && !orderTypes.has(row.orderType))
            return false;
        if (pops && !pops.has(row.areaPop))
            return false;
        if (statuses && !statuses.has(row.connectionStatus))
            return false;
        if (buckets && !buckets.has(ageBucketOf(row.ageDays)))
            return false;
        if (cities && !cities.has(row.city))
            return false;
        if (projectnames && !projectnames.has(row.projectname))
            return false;
        if (jvid && !row.jvid.toLowerCase().includes(jvid))
            return false;
        if (technician && !(row.technician ?? "").toLowerCase().includes(technician))
            return false;
        if (orderId && !row.orderId.toLowerCase().includes(orderId))
            return false;
        if (utac && !row.utac.toLowerCase().includes(utac))
            return false;
        if (address && !formatAddress(row).toLowerCase().includes(address))
            return false;
        if (filters.drawings === "open" && !row.drawingOpen)
            return false;
        if (filters.drawings === "hidden" && row.drawingOpen)
            return false;
        if (filters.executedFrom && row.executedAt.slice(0, 10) < filters.executedFrom)
            return false;
        if (filters.executedTo && row.executedAt.slice(0, 10) > filters.executedTo)
            return false;
        if (search) {
            const haystack = `${row.jvid} ${row.orderId} ${row.utac} ${row.streetName} ${row.city} ${row.technician ?? ""}`.toLowerCase();
            if (!haystack.includes(search))
                return false;
        }
        return true;
    });
}
/** A column filter's text, ready to match: trimmed, lower-cased, `null` when empty. */
function needle(value) {
    const trimmed = value?.trim().toLowerCase();
    return trimmed ? trimmed : null;
}
/**
 * Every column of the table, sortable. Ubi: *"all columns need sort and filter, visible to
 * all"* — so a contractor's table sorts on exactly the same fields as Unifiber's.
 * `contractor` only appears in the internal table; sorting a contractor's rows by it is
 * harmless and simply orders nothing.
 */
export const ORDER_SORT_FIELDS = [
    "jvid",
    "technician",
    "order",
    "type",
    "contractor",
    "address",
    "city",
    "pop",
    "drawing",
    "status",
    "utac",
    "executed",
    "age",
    "visibility",
];
/**
 * The direction a FIRST click on a heading applies — the convention `SortableTable` uses
 * everywhere else (`defaultDirectionForValue`): a number opens biggest-first, text A-to-Z.
 * So Age opens on the oldest order, which is the question clicking it asks. Executed opens
 * oldest-first too, being a date read as text — the same rows on top either way.
 */
export function firstClickDirection(field) {
    return field === "age" ? "desc" : "asc";
}
/**
 * Two identifiers held as digit strings, compared as numbers without ever becoming one.
 *
 * ⚠ A UTAC is 18 digits; `Number("543000297702930564")` is 543000297702930560 — the last
 * digits are gone and two different UTACs can compare equal. So: a shorter string of digits
 * is the smaller number, and at equal length digit strings compare correctly as text.
 * Anything that is not purely digits falls back to text, so a malformed value cannot throw.
 */
export function compareDigitStrings(a, b) {
    const digits = /^\d+$/;
    if (digits.test(a) && digits.test(b)) {
        const x = a.replace(/^0+(?=\d)/, "");
        const y = b.replace(/^0+(?=\d)/, "");
        if (x.length !== y.length)
            return x.length - y.length;
        return x < y ? -1 : x > y ? 1 : 0;
    }
    return a.localeCompare(b);
}
/**
 * Sort the filtered rows. `rowKey` breaks every tie, so paging can never skip or repeat one.
 *
 * ⚠ A row with NO contractor sorts LAST in both directions — the same rule `sortRowsBy`
 * applies to every other table in the product. "Not assigned" is not a value, and flipping
 * the sort must not float the twenty rows nobody owns above everybody's work.
 */
export function sortRows(rows, field = "jvid", direction = "asc") {
    const sign = direction === "desc" ? -1 : 1;
    const text = (a, b) => a.localeCompare(b, "fr-BE", { numeric: true });
    const compare = (a, b) => {
        switch (field) {
            case "order":
                return compareDigitStrings(a.orderId, b.orderId);
            case "utac":
                return compareDigitStrings(a.utac, b.utac);
            case "type":
                return text(orderTypeLabel(a.orderType), orderTypeLabel(b.orderType));
            case "contractor":
                // Both present here — the missing case is handled before the sign is applied.
                return text(a.contractor ?? "", b.contractor ?? "");
            case "technician":
                // Likewise; and case-blind, because the files mix "Samir Abidli" with "ODISSEI MIRZOYAN".
                return (a.technician ?? "").localeCompare(b.technician ?? "", "fr-BE", { sensitivity: "base" });
            case "address":
                // `numeric` so "Rue Haute 9" comes before "Rue Haute 10", as a reader expects.
                return text(formatAddress(a), formatAddress(b));
            case "city":
                return text(a.city, b.city);
            case "pop":
                return a.areaPop.localeCompare(b.areaPop);
            case "drawing":
                return a.projectname.localeCompare(b.projectname);
            case "status":
                return a.connectionStatus.localeCompare(b.connectionStatus);
            case "executed":
                return a.executedAt < b.executedAt ? -1 : a.executedAt > b.executedAt ? 1 : 0;
            case "age":
                return a.ageDays - b.ageDays;
            case "visibility":
                // Hidden (nobody), then open (one company), then shared (several) — the order the
                // table's three pills read in. A contractor's audience is cut to themselves by
                // `visibleRows`, so theirs sorts Hidden then Open.
                return Math.min(a.drawingAudience.length, 2) - Math.min(b.drawingAudience.length, 2);
            case "jvid":
            default:
                // Numeric, not lexicographic: a JVID is a bigint held as text, and "10009476"
                // must not sort before "1000844".
                return compareDigitStrings(a.jvid, b.jvid);
        }
    };
    return [...rows].sort((a, b) => {
        if (field === "contractor" && (a.contractor === null) !== (b.contractor === null)) {
            return a.contractor === null ? 1 : -1;
        }
        // No technician is not a value either: those rows stay at the bottom whichever way.
        if (field === "technician" && (a.technician === null) !== (b.technician === null)) {
            return a.technician === null ? 1 : -1;
        }
        return compare(a, b) * sign || a.rowKey.localeCompare(b.rowKey);
    });
}
/** How many POPs the chart draws before the rest are summarised. 14, as in screen 04. */
export const POP_CHART_SIZE = 14;
/** How many ISO weeks the delivered-per-week chart shows. 7, as in screen 04. */
export const WEEKLY_CHART_WEEKS = 7;
/**
 * Every figure on the screen, computed from ONE row set.
 *
 * ⚠ Everything — KPIs, all three charts, the table count — is derived here from the
 * rows the caller passes in. Nothing re-queries and nothing re-scopes. That is what
 * makes the header figure and the table reconcile for a contractor: they are literally
 * the same array.
 */
export function aggregate(rows) {
    const byContractor = {};
    const over90ByContractor = {};
    const jvids = new Set();
    const typeCounts = new Map();
    const statusCounts = new Map();
    const bucketCounts = new Map();
    const weekCounts = new Map();
    const popCounts = new Map();
    const cityCounts = new Map();
    const drawingCounts = new Map();
    let over90 = 0;
    let oldest = null;
    let rowsInHiddenDrawings = 0;
    for (const row of rows) {
        const contractor = row.contractor ?? "unassigned";
        byContractor[contractor] = (byContractor[contractor] ?? 0) + 1;
        jvids.add(row.jvid);
        if (row.ageDays > 90) {
            over90++;
            over90ByContractor[contractor] = (over90ByContractor[contractor] ?? 0) + 1;
        }
        if (!row.drawingOpen)
            rowsInHiddenDrawings++;
        if (oldest === null || row.executedAt < oldest)
            oldest = row.executedAt;
        typeCounts.set(row.orderType, (typeCounts.get(row.orderType) ?? 0) + 1);
        statusCounts.set(row.connectionStatus, (statusCounts.get(row.connectionStatus) ?? 0) + 1);
        cityCounts.set(row.city, (cityCounts.get(row.city) ?? 0) + 1);
        drawingCounts.set(row.projectname, (drawingCounts.get(row.projectname) ?? 0) + 1);
        const bucket = ageBucketOf(row.ageDays);
        bucketCounts.set(bucket, (bucketCounts.get(bucket) ?? 0) + 1);
        const executed = new Date(row.executedAt);
        const weekKey = isoWeekKey(executed);
        let week = weekCounts.get(weekKey);
        if (!week) {
            week = { label: isoWeek(executed).label, circet: 0, constructel: 0, total: 0 };
            weekCounts.set(weekKey, week);
        }
        week.total++;
        if (contractor === "circet")
            week.circet++;
        else if (contractor === "constructel")
            week.constructel++;
        let pop = popCounts.get(row.areaPop);
        if (!pop) {
            pop = { pop: row.areaPop, circet: 0, constructel: 0, unassigned: 0, total: 0 };
            popCounts.set(row.areaPop, pop);
        }
        pop.total++;
        if (contractor === "circet")
            pop.circet++;
        else if (contractor === "constructel")
            pop.constructel++;
        else
            pop.unassigned++;
    }
    const pops = [...popCounts.values()].sort((a, b) => b.total - a.total || a.pop.localeCompare(b.pop));
    const shown = pops.slice(0, POP_CHART_SIZE);
    const tail = pops.slice(POP_CHART_SIZE);
    const shownOrders = shown.reduce((sum, p) => sum + p.total, 0);
    return {
        total: rows.length,
        jvids: jvids.size,
        byContractor,
        over90,
        over90ByContractor,
        oldestExecutedAt: oldest,
        // Enumerated from AGE_BUCKETS rather than from what happened to occur, so an empty
        // bucket draws as a zero bar instead of vanishing and re-scaling the chart.
        ageBuckets: ageBucketRows(bucketCounts),
        weekly: [...weekCounts.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-WEEKLY_CHART_WEEKS)
            .map(([key, value]) => ({ key, ...value })),
        orderTypes: orderTypeRows(typeCounts),
        connectionStatuses: [...statusCounts.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([code, n]) => ({ code, n, tone: connectionStatusTone(code) })),
        byPop: {
            shown,
            all: pops.map((p) => ({ pop: p.pop, total: p.total })),
            totalPops: pops.length,
            mixedPops: pops.filter((p) => Number(p.circet > 0) + Number(p.constructel > 0) + Number(p.unassigned > 0) > 1).length,
            shownOrders,
            tailPops: tail.length,
            tailOrders: tail.reduce((sum, p) => sum + p.total, 0),
        },
        rowsInHiddenDrawings,
        facets: {
            cities: facetRows(cityCounts, (a, b) => a.localeCompare(b, "fr-BE")),
            drawings: facetRows(drawingCounts, (a, b) => a.localeCompare(b)),
        },
    };
}
function facetRows(counts, order) {
    return [...counts.entries()].sort((a, b) => order(a[0], b[0])).map(([value, n]) => ({ value, n }));
}
/**
 * The order-type chips, in the order screen 04 lists them.
 *
 * ⚠ A code DMP adds that the mockups never saw is appended rather than dropped. The
 * chips are read as a breakdown of the headline figure, so a silently missing type
 * would make the report fail to reconcile with itself — and nobody would know why.
 */
function orderTypeRows(counts) {
    const known = ORDER_TYPES.filter((t) => counts.has(t.code)).map((t) => ({
        code: t.code,
        label: t.label,
        n: counts.get(t.code) ?? 0,
        tone: t.tone,
    }));
    const unknown = [...counts.keys()]
        .filter((code) => !ORDER_TYPES.some((t) => t.code === code))
        .sort()
        .map((code) => ({ code, label: code, n: counts.get(code) ?? 0, tone: "slate" }));
    return [...known, ...unknown];
}
function ageBucketRows(counts) {
    // Imported lazily through the module's own constant to keep one source for the list.
    return AGE_BUCKET_ROWS.map((b) => ({ label: b.label, n: counts.get(b.label) ?? 0, tone: b.tone }));
}
const AGE_BUCKET_ROWS = [
    { label: "0–7 days", tone: "emerald" },
    { label: "8–30 days", tone: "blue" },
    { label: "31–90 days", tone: "amber" },
    { label: "91–180 days", tone: "orange" },
    { label: "181–365 days", tone: "orange" },
    { label: "Over a year", tone: "red" },
];
export const EXPORT_COLUMNS = [
    { header: "JVID", kind: "id", width: 12, value: (r) => r.jvid },
    // Beside the JVID, as on screen. Already blanked on another contractor's rows by
    // `visibleRows`, so a contractor's file carries their own technicians' names only.
    { header: "Technician", kind: "text", width: 24, value: (r) => r.technician },
    { header: "Order", kind: "id", width: 11, value: (r) => r.orderId },
    { header: "UTAC", kind: "id", width: 21, value: (r) => r.utac },
    { header: "Type", kind: "text", width: 15, value: (r) => orderTypeLabel(r.orderType) },
    { header: "Contractor", kind: "text", width: 13, value: (r) => r.contractor, internalOnly: true },
    { header: "Address", kind: "text", width: 40, value: (r) => formatAddress(r) },
    { header: "City", kind: "text", width: 18, value: (r) => r.city },
    { header: "POP", kind: "text", width: 12, value: (r) => r.areaPop },
    { header: "Drawing", kind: "text", width: 17, value: (r) => r.projectname },
    // The flag the screen shows, so a file forwarded to a contractor's teams still says which
    // orders sit in a drawing Unifiber has not opened to them. A contractor's file only:
    // Unifiber's holds open drawings alone (`exportRows`), where it could only say "Open".
    {
        header: "Drawing state",
        kind: "text",
        width: 14,
        value: (r) => (r.drawingOpen ? "Open" : "Hidden"),
        contractorOnly: true,
    },
    { header: "Status", kind: "text", width: 9, value: (r) => r.connectionStatus },
    { header: "Executed", kind: "date", width: 13, value: (r) => isoDateUtc(new Date(r.executedAt)) },
    { header: "Age (days)", kind: "number", width: 11, value: (r) => r.ageDays },
];
/**
 * The rows the Excel export may contain.
 *
 * **Unifiber: rows in OPEN drawings, and nothing else.** Ubi, Cocon stats reporting feedback
 * (Sep 2026): *"Export to excel must contain open drawings only"*. Decided with Hugues:
 * always, whatever the drawings filter on the screen says.
 *
 * **A contractor: every row their table shows**, hidden ones included and flagged in the
 * "Drawing state" column. Decided with Hugues, 13 Oct 2026, when contractors began to see
 * their own orders in hidden drawings: the file follows the screen's filter, so a contractor
 * who wants open drawings only picks "Open only" first.
 *
 * ⚠ For Unifiber this is the one deliberate difference between the table and the file, and
 * it only ever narrows. Hard rule 9 still holds: the export is `selectRows` — the very rows
 * the table renders under the same filters — and then this. Because it can make Unifiber's
 * file shorter than the table, the screen states the exact count before the click
 * (`exportCount` on the API).
 */
export function exportRows(rows, options) {
    return options.internal ? rows.filter((row) => row.drawingOpen) : [...rows];
}
/**
 * The export as a table of typed cells — the columns this viewer may see, and one line per
 * row. Pure, so what goes into the file is tested without a zip in sight; the API turns it
 * into a workbook.
 */
export function exportTable(rows, options) {
    const columns = EXPORT_COLUMNS.filter((c) => (options.internal ? !c.contractorOnly : !c.internalOnly));
    return { columns, cells: rows.map((row) => columns.map((c) => c.value(row))) };
}
/**
 * `order-activation_circet_2026-09-17.xlsx`.
 *
 * The scope and the data date are in the name because these files get mailed on, and a
 * file called `export.csv` tells the next reader nothing about which day it describes —
 * which matters especially here, where the whole product is about agreeing on a day.
 */
export function exportFilename(scopeLabel, runDate) {
    const safe = scopeLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "all";
    return `order-activation_${safe}_${runDate}.xlsx`;
}
/** Derive `ageDays` for a stored row. Kept here so the API and any backfill agree. */
export function computeAge(executedAt, snapshotDate) {
    return ageInDays(executedAt, snapshotDate);
}
//# sourceMappingURL=order-activation.js.map